import React, { useRef, useState, useCallback, useEffect } from 'react'
import { MarketViewSpec } from '../../services/api'
import { SpecCriterion } from './SpecCriteriaPanel'
import { polarityZones, ZoneRole } from '../../lib/specPolarity'

interface NumericCriteriaSliderProps {
  spec: MarketViewSpec
  criterion: SpecCriterion
  onChange: (updates: Partial<SpecCriterion>) => void
  /** When false the slider direction flips: left = desirable, right = failure. Default true. */
  higherIsBetter?: boolean
}

// Raw-palette rendering of each zone role. The roles come from `polarityZones`,
// shared with the scorecard's ThresholdRangeBar (which paints them with design
// tokens instead) so there is one definition of which side means what.
const ZONE_GRADIENT_LEFT: Record<ZoneRole, string> = {
  fail: 'from-red-400/80 to-red-300/60',
  acceptable: 'from-amber-300/60 to-amber-200/60',
  desirable: 'from-emerald-400/80 to-emerald-300/60',
}

const ZONE_GRADIENT_RIGHT: Record<ZoneRole, string> = {
  fail: 'from-red-300/60 to-red-400/80',
  acceptable: 'from-amber-300/60 to-amber-200/60',
  desirable: 'from-emerald-300/60 to-emerald-400/80',
}

const ZONE_HANDLE: Record<ZoneRole, string> = {
  fail: 'bg-red-500 hover:bg-red-600',
  acceptable: 'bg-amber-500 hover:bg-amber-600',
  desirable: 'bg-emerald-500 hover:bg-emerald-600',
}

const ZONE_HANDLE_ACTIVE: Record<ZoneRole, string> = {
  fail: 'bg-red-600',
  acceptable: 'bg-amber-600',
  desirable: 'bg-emerald-600',
}

const ZONE_LABEL: Record<ZoneRole, string> = {
  fail: 'text-red-500',
  acceptable: 'text-amber-600',
  desirable: 'text-emerald-600',
}

const ZONE_LABEL_ACTIVE: Record<ZoneRole, string> = {
  fail: 'text-red-600 font-semibold scale-110',
  acceptable: 'text-amber-700 font-semibold scale-110',
  desirable: 'text-emerald-700 font-semibold scale-110',
}

/**
 * Two-handle slider for lower_threshold / upper_threshold.
 *
 * The slider always positions lower_threshold on the left and upper_threshold
 * on the right; `higherIsBetter` flips only the zone colours. See
 * `lib/specPolarity` for the contract.
 */
const NumericCriteriaSlider: React.FC<NumericCriteriaSliderProps> = ({
  spec,
  criterion,
  onChange,
  higherIsBetter = true,
}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'lower' | 'upper' | null>(null)

  // Coerce to number — Django CharField/DecimalField may serialize as strings
  const min = Number(spec.typical_min) || 0
  const max = Number(spec.typical_max) || 100
  const range = max - min || 1

  // Two thresholds always ordered lower < upper on the number line
  const lowerThreshold = Number(criterion.lower_threshold) || min + range * 0.25
  const upperThreshold = Number(criterion.upper_threshold) || min + range * 0.75

  const valueToPercent = (value: number) => ((value - min) / range) * 100
  const percentToValue = (percent: number) => min + (percent / 100) * range

  const formatValue = (value: number | string) => {
    const n = Number(value)
    if (isNaN(n)) return String(value)
    if (Math.abs(n) >= 100) return Math.round(n).toString()
    return n.toFixed(1)
  }

  const getPositionPercent = useCallback((clientX: number) => {
    if (!trackRef.current) return 0
    const rect = trackRef.current.getBoundingClientRect()
    return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
  }, [])

  const handleMove = useCallback((clientX: number) => {
    if (!dragging) return
    const percent = getPositionPercent(clientX)
    const value = percentToValue(percent)

    if (dragging === 'lower') {
      const clamped = Math.min(value, upperThreshold - range * 0.05)
      onChange({ lower_threshold: Math.round(clamped * 10) / 10 })
    } else {
      const clamped = Math.max(value, lowerThreshold + range * 0.05)
      onChange({ upper_threshold: Math.round(clamped * 10) / 10 })
    }
  }, [dragging, getPositionPercent, percentToValue, lowerThreshold, upperThreshold, range, onChange])

  useEffect(() => {
    if (!dragging) return
    const onMove = (e: MouseEvent) => handleMove(e.clientX)
    const onTouch = (e: TouchEvent) => { if (e.touches.length > 0) handleMove(e.touches[0].clientX) }
    const onEnd = () => setDragging(null)

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onEnd)
    document.addEventListener('touchmove', onTouch)
    document.addEventListener('touchend', onEnd)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onEnd)
      document.removeEventListener('touchmove', onTouch)
      document.removeEventListener('touchend', onEnd)
    }
  }, [dragging, handleMove])

  const loPct = valueToPercent(lowerThreshold)
  const hiPct = valueToPercent(upperThreshold)
  const unit = spec.unit || ''

  // Zone roles flip with direction; the thresholds themselves never move.
  const zones = polarityZones(higherIsBetter)

  const leftColor = ZONE_GRADIENT_LEFT[zones.left]
  const middleColor = ZONE_GRADIENT_RIGHT[zones.middle]
  const rightColor = ZONE_GRADIENT_RIGHT[zones.right]

  // Handle accent colours: lower handle = left zone colour, upper = right
  const lowerHandleColor = ZONE_HANDLE[zones.left]
  const lowerHandleActive = ZONE_HANDLE_ACTIVE[zones.left]
  const upperHandleColor = ZONE_HANDLE[zones.right]
  const upperHandleActive = ZONE_HANDLE_ACTIVE[zones.right]

  // Label text colours
  const lowerLabelColor = ZONE_LABEL[zones.left]
  const lowerLabelActive = ZONE_LABEL_ACTIVE[zones.left]
  const upperLabelColor = ZONE_LABEL[zones.right]
  const upperLabelActive = ZONE_LABEL_ACTIVE[zones.right]

  return (
    <div className="py-1 space-y-1">
      {/* Min/Max labels */}
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{formatValue(min)}{unit && ` ${unit}`}</span>
        <span>{formatValue(max)}{unit && ` ${unit}`}</span>
      </div>

      {/* Track */}
      <div
        ref={trackRef}
        className="relative h-2.5 rounded-full overflow-hidden cursor-pointer select-none"
      >
        {/* Left zone */}
        <div
          className={`absolute top-0 bottom-0 left-0 bg-gradient-to-r ${leftColor}`}
          style={{ width: `${loPct}%` }}
        />
        {/* Middle (acceptable) zone */}
        <div
          className={`absolute top-0 bottom-0 bg-gradient-to-r ${middleColor}`}
          style={{ left: `${loPct}%`, width: `${hiPct - loPct}%` }}
        />
        {/* Right zone */}
        <div
          className={`absolute top-0 bottom-0 right-0 bg-gradient-to-r ${rightColor}`}
          style={{ left: `${hiPct}%` }}
        />

        {/* Lower handle */}
        <div
          className="absolute top-0 bottom-0 w-4 cursor-ew-resize flex items-center justify-center z-10"
          style={{ left: `calc(${loPct}% - 8px)` }}
          onMouseDown={(e) => { e.preventDefault(); setDragging('lower') }}
          onTouchStart={(e) => { e.preventDefault(); setDragging('lower') }}
        >
          <div className={`w-0.5 h-full rounded-full transition-all ${
            dragging === 'lower' ? `${lowerHandleActive} w-1` : `${lowerHandleColor} hover:w-1`
          }`} />
        </div>

        {/* Upper handle */}
        <div
          className="absolute top-0 bottom-0 w-4 cursor-ew-resize flex items-center justify-center z-10"
          style={{ left: `calc(${hiPct}% - 8px)` }}
          onMouseDown={(e) => { e.preventDefault(); setDragging('upper') }}
          onTouchStart={(e) => { e.preventDefault(); setDragging('upper') }}
        >
          <div className={`w-0.5 h-full rounded-full transition-all ${
            dragging === 'upper' ? `${upperHandleActive} w-1` : `${upperHandleColor} hover:w-1`
          }`} />
        </div>
      </div>

      {/* Threshold value labels */}
      <div className="relative h-4">
        <span
          className={`absolute text-[10px] tabular-nums transition-all ${
            dragging === 'lower' ? lowerLabelActive : lowerLabelColor
          }`}
          style={{ left: `${loPct}%`, transform: 'translateX(-50%)' }}
        >
          {formatValue(lowerThreshold)}
        </span>
        <span
          className={`absolute text-[10px] tabular-nums transition-all ${
            dragging === 'upper' ? upperLabelActive : upperLabelColor
          }`}
          style={{ left: `${hiPct}%`, transform: 'translateX(-50%)' }}
        >
          {formatValue(upperThreshold)}
        </span>
      </div>
    </div>
  )
}

export default NumericCriteriaSlider
